import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'
import useChartData from './useChartData'
import { clampEndIdx, rescaleFracPx } from '../../chart/machines/xWindow'
import { loadXWin } from '../../chart/machines/xZoomStore'

/**
 * useChartSlot — ONE chart instance: its per-chart settings (interval,
 * bucket, autoscroll, view window) plus its own useChartData WebSocket
 * connection, bundled as a unit because they're coupled (the data hook
 * consumes the settings and their refs).
 *
 * This is the multi-chart foundation (stage 1 of 3):
 *   1. App calls useChartSlot once and destructures — behavior-identical
 *      to the old inline cluster. (this stage)
 *   2. App calls it twice ('A'/'B'), renders two FootprintCharts from the
 *      two slots' props, and `activeChartId` picks which slot's `data`
 *      feeds the satellites (tape, trading, mint-click routing). Focus is
 *      a POINTER — switching never resubscribes either socket.
 *   3. Gold-chip/amber focus treatment (pure CSS off a host class).
 *
 * A HOOK rather than a ChartHost component on purpose: satellites in App
 * need the slot's `data` object, and a component would have to report it
 * upward via onData callbacks — a render-loop hazard since the hook
 * return isn't referentially stable. Fixed hook-call count (one per
 * slot, declared in App) keeps the rules of hooks honest.
 *
 * `chartId` keys nothing yet — it's the handle stage 2 uses for per-slot
 * settings persistence (localStorage `solcheddar:slot:<id>:*`) and the
 * focus pointer.
 *
 * @param {string} chartId            stable slot id ('main', 'B', ...)
 * @param {object} deps
 * @param {object} deps.publicKeyRef  ref mirroring the connected pubkey
 * @param {func}   deps.showToast     app-level toast
 */
export default function useChartSlot(chartId, { publicKeyRef, showToast }) {
  // Mode is PER-SLOT: each chart is independently solana or hyperliquid,
  // with its own selected pair — chart 1 on a pump mint while chart 2
  // rides an HL pair is legal. The header controls (search, FUTURES
  // dropdown) operate on the ACTIVE slot via App's derived bindings.
  //
  // SEEDED FROM THE LAST VIEW. App's boot restore already reopens a futures
  // pair (cheddar_last_view -> pickFutByCoin), but it can only run once the
  // socket reports 'connected'. Until then the slot sat in 'solana' — so a
  // refresh on a futures chart rendered the whole app in Solana mode first
  // (header, wallet area, search behaviour, chart branding) and snapped to
  // futures a beat later. Seeding the INITIAL state from the same key means
  // frame 1 is already the right mode. This does NOT load data — the socket
  // gate stays exactly where it is, and selectHlPair remains the one path
  // that fetches history and subscribes. It only stops the wrong-mode paint.
  //
  // Guards:
  //  • main slot only. cheddar_last_view tracks the MAIN chart; seeding B
  //    from it would open the same pair on both, which is the "pair switch
  //    changes both charts" failure in a different costume.
  //  • a /main/<mint> deep link is explicit intent and outranks the
  //    remembered view — App's restore prioritises it too, and seeding
  //    futures here would just cause a futures->solana flip on arrival.
  const [dataSource, setDataSource] = useState(() => {
    if (chartId !== 'main') return 'solana'
    try {
      if (/^\/main\/.+/.test(window.location.pathname)) return 'solana'
      const lv = JSON.parse(localStorage.getItem('cheddar_last_view') || 'null')
      return lv?.kind === 'fut' && lv.coin ? 'hyperliquid' : 'solana'
    } catch { return 'solana' }
  })
  // Minimal synthesized pair — the SAME shape pickFutByCoin falls back to
  // ({base, symbol}) before the lazy hlPairs fetch lands, and App's existing
  // heal-in-place effect upgrades it with szDecimals/maxLeverage when the
  // real list arrives. Seeding it keeps the header chip from rendering an
  // empty pair for the moments before the boot restore fires.
  const [hlPair, setHlPair] = useState(() => {
    if (chartId !== 'main') return null
    try {
      if (/^\/main\/.+/.test(window.location.pathname)) return null
      const lv = JSON.parse(localStorage.getItem('cheddar_last_view') || 'null')
      if (lv?.kind === 'fut' && lv.coin) {
        const c = String(lv.coin).toUpperCase()
        return { base: c, symbol: c }
      }
    } catch {}
    return null
  })

  const [timeInterval, setTimeInterval] = useState(60)
  const [mcapBucketSize, setMcapBucketSize] = useState(100)
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true)
  // (xzoom) Reload: start at the user's stored candle count for this slot +
  // device class (xZoomStore); 50 is the pre-fit placeholder otherwise.
  const [viewWindowSize, setViewWindowSize] = useState(() => loadXWin(chartId) ?? 50)
  const [viewEndIndex, setViewEndIndex] = useState(-1)

  // chartMode + bucketDensity are PER-SLOT (lifted out of FootprintChart's
  // internal state) so the shared header controls can drive whichever chart is
  // active — same routing as timeInterval. localStorage holds the last-used
  // value as the default for new slots (the old chart-internal behavior).
  const [chartMode, setChartMode] = useState(
    () => localStorage.getItem('solcheddar_chart_mode') || 'footprint'
  )
  const [bucketDensity, setBucketDensity] = useState(
    () => localStorage.getItem('solcheddar_bucket_density') || 'normal'
  )
  useEffect(() => { localStorage.setItem('solcheddar_chart_mode', chartMode) }, [chartMode])
  // tpotf-20261103: TPO runs on 30m. Entering TPO remembers the timeframe and
  // switches to 30m; leaving restores it (unless the user changed it meanwhile).
  const tpoPrevTfRef = useRef(null)
  useEffect(() => {
    if (chartMode === 'tpo') {
      if (tpoPrevTfRef.current == null) { tpoPrevTfRef.current = timeInterval; if (timeInterval !== 1800) setTimeInterval(1800) }
    } else if (tpoPrevTfRef.current != null) {
      const prev = tpoPrevTfRef.current; tpoPrevTfRef.current = null
      setTimeInterval(cur => (cur === 1800 ? prev : cur))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartMode])
  useEffect(() => { localStorage.setItem('solcheddar_bucket_density', bucketDensity) }, [bucketDensity])

  const bodyScrollRef = useRef(null)
  const viewWindowSizeRef = useRef(viewWindowSize)
  const viewEndIndexRef = useRef(viewEndIndex)
  // Sub-candle pan offset (px). Was mirrored inside FPCC; the geom snapshot
  // needs frac next to win/end, so the slot owns the ref and commitWindow
  // renormalizes it when W changes.
  const fracPxRef = useRef(0)
  const timeIntervalRef = useRef(timeInterval)
  const scrollToLatestRef = useRef(null)
  // Body-wheel zoom parks a real end. While this is set, follow (-1 / the
  // live pad) must not copy itself back over that end. Cleared only when
  // commitEnd is asked for the follow sentinel.
  const wheelParkRef = useRef(false)

  useEffect(() => { viewWindowSizeRef.current = viewWindowSize }, [viewWindowSize])
  useLayoutEffect(() => {
    if (wheelParkRef.current) return
    viewEndIndexRef.current = viewEndIndex
  }, [viewEndIndex])
  useEffect(() => { timeIntervalRef.current = timeInterval }, [timeInterval])

  // ── X write surface (geometry half is pure xWindow.js) ─────────────────
  // One commit path: clamp through the walls, then write ref + state in the
  // same turn. FPCC used to mirror viewEndIndex in its own ref and sometimes
  // write only one of the two — after total shrinks, drag and paint disagreed.
  // Frac lives on fracPxRef here; commitWindow(size, total, plotW) renormalizes it.
  const commitEnd = useCallback((endIdx, total, opts = null) => {
    // -1 is the explicit return to follow (footer reset, token switch,
    // scroll-to-latest). A parked body-wheel end stops being protected.
    if (endIdx === -1) wheelParkRef.current = false
    // (drag-jump) opts.clampFuture === false: a body drag that STARTED on a
    // wheel park past the forward wall keeps that end instead of snapping it
    // back onto the wall (the drag bounds it by its own start end).
    const next = clampEndIdx(endIdx, total, viewWindowSizeRef.current, !(opts && opts.clampFuture === false))
    viewEndIndexRef.current = next
    setViewEndIndex(next)
    return next
  }, [])

  const commitWindow = useCallback((size, total, plotW = 0, opts = null) => {
    const prevW = Math.max(1, +viewWindowSizeRef.current || 1)
    const w = Math.max(1, Math.round(+size || 1))
    // Ref always tracks the legal size. State only when the INT changes —
    // X zoom glide calls this every frame with a float; rounding to the same
    // candle count must not setState (parent re-render → effect churn).
    viewWindowSizeRef.current = w
    // (xref) Glide frames pass { deferState: true }: this state lives in App,
    // so every integer candle step re-rendered ALL of App (header, scan panel,
    // the other chart...). The chart holds the per-step value locally while
    // the glide runs and commits here once, at landing (FPCC flushGlideWin).
    // Ref and end clamp below are still per frame. Anchor skips frac rescale.
    if (!(opts && opts.deferState)) setViewWindowSize(prev => (prev === w ? prev : w))
    // Body-wheel passes opts.anchor { end, frac } already solved for this W.
    // Rescale assumes a frozen end and would slide the candle under the cursor.
    const anchored = opts && opts.anchor && Number.isFinite(opts.anchor.end)
    if (anchored) {
      // Park before the write. Trade ticks and the state mirror read this
      // and must not put the follow sentinel back on top of the anchor.
      wheelParkRef.current = true
      // pastRight: cursor zoom has gone past the live edge. Do not pull
      // that end back onto the forward wall. Follow (-1) is not this path.
      const end = clampEndIdx(opts.anchor.end, total, w, !opts.anchor.pastRight)
      viewEndIndexRef.current = end
      // Compare to STATE, not the ref. The ref can already be the resolved
      // live pad while state is still -1; skipping then left follow in charge.
      setViewEndIndex(prev => (prev === end ? prev : end))
      if (Number.isFinite(opts.anchor.frac)) fracPxRef.current = opts.anchor.frac
      return { win: w, end, frac: fracPxRef.current }
    }
    const end = clampEndIdx(viewEndIndexRef.current, total, w)
    if (end !== viewEndIndexRef.current) {
      viewEndIndexRef.current = end
      setViewEndIndex(end)
    }
    // Frac rescale owns the sub-candle slide across a W change. plotW=0 skips
    // (caller has no layout yet); publishWin always passes the live plot width.
    if (plotW > 0 && prevW !== w) {
      fracPxRef.current = rescaleFracPx(fracPxRef.current, prevW, w, plotW)
    }
    return { win: w, end, frac: fracPxRef.current }

  }, [])

  // One handle for the geom snapshot + FPCC: win / end / frac + the two commits.
  // Loose setters stay on the slot return for App header controls; the chart
  // should take `xWin` and stop threading five X props.
  const xWin = useMemo(() => ({
    windowSize: viewWindowSize,
    endIndex: viewEndIndex,
    windowSizeRef: viewWindowSizeRef,
    endIndexRef: viewEndIndexRef,
    fracPxRef,
    wheelParkRef,
    commitEnd,
    commitWindow,
    setWindowSize: setViewWindowSize,
    setEndIndex: setViewEndIndex,
    snap: () => ({
      win: viewWindowSizeRef.current,
      end: viewEndIndexRef.current,
      frac: fracPxRef.current,
    }),
  }), [viewWindowSize, viewEndIndex, commitEnd, commitWindow])

  const data = useChartData({
    chartId,
    publicKeyRef,
    mcapBucketSize,
    setMcapBucketSize,
    timeInterval,
    autoScrollEnabled,
    viewWindowSizeRef,
    bodyScrollRef,
    showToast,
    scrollToLatest: scrollToLatestRef.current,
    onOwnBuyDetected: null,
    // The price grid is the venue's, not ours. szDecimals rides on the
    // pair the header and trading already use; the data layer was the one
    // place that decided the grid without being told what it is.
    hlPair,
  })

  return {
    chartId,
    data,
    dataSource, setDataSource,
    hlPair, setHlPair,
    timeInterval, setTimeInterval,
    mcapBucketSize, setMcapBucketSize,
    chartMode, setChartMode,
    bucketDensity, setBucketDensity,
    autoScrollEnabled, setAutoScrollEnabled,
    viewWindowSize, setViewWindowSize,
    viewEndIndex, setViewEndIndex,
    viewEndIndexRef,
    fracPxRef,
    commitEnd,
    commitWindow,
    xWin,
    bodyScrollRef,
    viewWindowSizeRef,
    timeIntervalRef,
    scrollToLatestRef,
  }
}